import type { EventPayload } from '@side/analytics';

export enum TrackReferralsEvent {
  ReferralsLanding,
  ClickCommunityBanner,
  CreateReferral,
  ClickAddReferralButton,
  AddReferralLanding,
  CommunityFeatureBannerUnmount,
  CommunityFeatureBannerMount,
  SelectReferralType,
  ClickViewReferralOnListPage,
  PageView,
  ClickDocumentsButtonOnAgreementPage,
  ClickEditReferralOverview,
  ClickEditReferralAgreement,
  SelectReferralFeeType,
  EditReferralAgreementField,
  ClickCreateTransactionButton,
  EditClientField,
  ClickEnterContactManually,
  SelectCounterPartyAgentSuggestion,
  SelectPrimaryAgentSuggestion,
  SelectClientAutocomplete,
  SelectClientType,
  ClearAllClientTypes,
  ClickSaveGoogleContactButton,
  ClickEditContact,
  ClickRemoveContact,
  ClickCloseEditor,
  ClickCreateTransactionMenuItem,
  ClickContinueEditingButton,
  ClickFiltersButton,
  FilterModalMount,
  FilterModalUnmount,
  UpdateFilter,
  ClickClearAllFilters,
}

type EventTypeLabels = Record<TrackReferralsEvent, string>;

export const ReferralsEventLabels: EventTypeLabels = {
  [TrackReferralsEvent.ReferralsLanding]: 'Referrals',
  [TrackReferralsEvent.ClickCommunityBanner]: 'Referrals',
  [TrackReferralsEvent.CreateReferral]: 'Referrals',
  [TrackReferralsEvent.ClickAddReferralButton]: 'Referrals',
  [TrackReferralsEvent.AddReferralLanding]: 'Referrals',
  [TrackReferralsEvent.CommunityFeatureBannerUnmount]: 'Referrals',
  [TrackReferralsEvent.CommunityFeatureBannerMount]: 'Referrals',
  [TrackReferralsEvent.SelectReferralType]: 'Referrals',
  [TrackReferralsEvent.ClickViewReferralOnListPage]: 'Referrals',
  [TrackReferralsEvent.PageView]: 'Referrals',
  [TrackReferralsEvent.ClickDocumentsButtonOnAgreementPage]: 'Referrals',
  [TrackReferralsEvent.ClickEditReferralOverview]: 'Referrals',
  [TrackReferralsEvent.ClickEditReferralAgreement]: 'Referrals',
  [TrackReferralsEvent.SelectReferralFeeType]: 'Referrals',
  [TrackReferralsEvent.EditReferralAgreementField]: 'Referrals',
  [TrackReferralsEvent.ClickCreateTransactionButton]: 'Referrals',
  [TrackReferralsEvent.EditClientField]: 'Referrals',
  [TrackReferralsEvent.ClickEnterContactManually]: 'Referrals',
  [TrackReferralsEvent.SelectCounterPartyAgentSuggestion]: 'Referrals',
  [TrackReferralsEvent.SelectPrimaryAgentSuggestion]: 'Referrals',
  [TrackReferralsEvent.SelectClientAutocomplete]: 'Referrals',
  [TrackReferralsEvent.SelectClientType]: 'Referrals',
  [TrackReferralsEvent.ClearAllClientTypes]: 'Referrals',
  [TrackReferralsEvent.ClickSaveGoogleContactButton]: 'Referrals',
  [TrackReferralsEvent.ClickEditContact]: 'Referrals',
  [TrackReferralsEvent.ClickRemoveContact]: 'Referrals',
  [TrackReferralsEvent.ClickCloseEditor]: 'Referrals',
  [TrackReferralsEvent.ClickCreateTransactionMenuItem]: 'Referrals',
  [TrackReferralsEvent.ClickContinueEditingButton]: 'Referrals',
  [TrackReferralsEvent.ClickFiltersButton]: 'Referrals',
  [TrackReferralsEvent.FilterModalMount]: 'Referrals',
  [TrackReferralsEvent.FilterModalUnmount]: 'Referrals',
  [TrackReferralsEvent.UpdateFilter]: 'Referrals',
  [TrackReferralsEvent.ClickClearAllFilters]: 'Referrals',
};

type EventTypePayloads = Record<TrackReferralsEvent, EventPayload>;

export const ReferralsEventMap: EventTypePayloads = {
  [TrackReferralsEvent.ReferralsLanding]: {
    eventContainer: 'referrals_list_view',
    eventTarget: 'referrals_list_view',
    eventType: 'load',
    task: 'view',
  },
  [TrackReferralsEvent.ClickCommunityBanner]: {
    eventContainer: 'community_cta_banner',
    eventTarget: '',
    eventType: 'click',
    task: 'navigate',
  },
  [TrackReferralsEvent.CreateReferral]: {
    eventContainer: 'add_referrals_view',
    eventTarget: 'create_referral_button',
    eventType: 'click',
    task: 'add',
  },
  [TrackReferralsEvent.ClickAddReferralButton]: {
    eventContainer: 'referrals_list_view',
    eventTarget: '',
    eventType: 'click',
    task: 'navigate',
  },
  [TrackReferralsEvent.AddReferralLanding]: {
    eventContainer: 'add_referrals_view',
    eventTarget: 'add_referrals_view',
    eventType: 'load',
    task: 'view',
  },
  [TrackReferralsEvent.CommunityFeatureBannerUnmount]: {
    eventContainer: 'community_cta_banner',
    eventTarget: 'close_button',
    eventType: 'click',
    task: 'view',
  },
  [TrackReferralsEvent.CommunityFeatureBannerMount]: {
    eventContainer: 'referrals_list_view',
    eventTarget: 'community_cta_banner',
    eventType: 'load',
    task: 'view',
  },
  [TrackReferralsEvent.SelectReferralType]: {
    eventContainer: 'agent_details_card',
    eventTarget: 'referral_type_button',
    eventType: 'click',
    task: 'select',
  },
  [TrackReferralsEvent.ClickViewReferralOnListPage]: {
    eventContainer: 'referrals_list_view',
    eventTarget: '',
    eventType: 'click',
    task: 'navigate',
  },
  [TrackReferralsEvent.PageView]: {
    eventContainer: 'referral_detail_view',
    eventTarget: '',
    eventType: 'load',
    task: 'view',
  },
  [TrackReferralsEvent.ClickDocumentsButtonOnAgreementPage]: {
    eventContainer: 'referral_detail_view',
    eventTarget: 'documents_button',
    eventType: 'click',
    task: 'navigate',
  },
  [TrackReferralsEvent.ClickEditReferralOverview]: {
    eventContainer: 'referral_detail_view',
    eventTarget: 'edit_referral_button',
    eventType: 'click',
    task: 'navigate',
  },
  [TrackReferralsEvent.ClickEditReferralAgreement]: {
    eventContainer: 'referral_detail_view',
    eventTarget: 'edit_agreement_button',
    eventType: 'click',
    task: 'navigate',
  },
  [TrackReferralsEvent.SelectReferralFeeType]: {
    eventContainer: 'referral_detail_view',
    eventTarget: 'referral_fee',
    eventType: 'click',
    task: 'select',
  },
  [TrackReferralsEvent.EditReferralAgreementField]: {
    eventContainer: 'referral_detail_view',
    eventTarget: '',
    eventType: 'blur',
    task: 'modify_input',
  },
  [TrackReferralsEvent.ClickCreateTransactionButton]: {
    eventContainer: '',
    eventTarget: 'create_transaction_button',
    eventType: 'click',
    task: 'select',
  },
  [TrackReferralsEvent.EditClientField]: {
    eventContainer: 'client_details_card',
    eventTarget: '',
    eventType: 'blur',
    task: 'modify_input',
  },
  [TrackReferralsEvent.ClickEnterContactManually]: {
    eventContainer: 'transacting_agent_dropdown',
    eventTarget: 'manual_contact_button',
    eventType: 'click',
    task: 'select',
  },
  [TrackReferralsEvent.SelectCounterPartyAgentSuggestion]: {
    eventContainer: 'agent_details_card',
    eventTarget: '',
    eventType: 'click',
    task: 'select',
  },
  [TrackReferralsEvent.SelectPrimaryAgentSuggestion]: {
    eventContainer: 'agent_details_card',
    eventTarget: '',
    eventType: 'click',
    task: 'select',
  },
  [TrackReferralsEvent.SelectClientAutocomplete]: {
    eventContainer: 'client_details_card',
    eventTarget: 'contact_auto_populate',
    eventType: 'click',
    task: 'modify_input',
  },
  [TrackReferralsEvent.SelectClientType]: {
    eventContainer: 'client_details_card',
    eventTarget: 'client_type_item',
    eventType: 'click',
    task: '',
  },
  [TrackReferralsEvent.ClearAllClientTypes]: {
    eventContainer: 'client_details_card',
    eventTarget: 'clear_client_type_button',
    eventType: 'click',
    task: 'update',
  },
  [TrackReferralsEvent.ClickSaveGoogleContactButton]: {
    eventContainer: 'agent_details_card',
    eventTarget: 'save_google_contact_button',
    eventType: 'click',
    task: 'update',
  },
  [TrackReferralsEvent.ClickEditContact]: {
    eventContainer: 'agent_details_card',
    eventTarget: 'edit_contact_button',
    eventType: 'click',
    task: 'select',
  },
  [TrackReferralsEvent.ClickRemoveContact]: {
    eventContainer: 'agent_details_card',
    eventTarget: 'remove_contact_button',
    eventType: 'click',
    task: 'select',
  },
  [TrackReferralsEvent.ClickCloseEditor]: {
    eventContainer: 'referral_detail_view',
    eventTarget: 'close_editor_button',
    eventType: 'click',
    task: 'navigate',
  },
  [TrackReferralsEvent.ClickCreateTransactionMenuItem]: {
    eventContainer: 'create_transaction_dropdown',
    eventTarget: '',
    eventType: 'click',
    task: 'navigate',
  },
  [TrackReferralsEvent.ClickContinueEditingButton]: {
    eventContainer: 'referral_detail_view',
    eventTarget: 'continue_editing_button',
    eventType: 'click',
    task: 'navigate',
  },
  [TrackReferralsEvent.ClickFiltersButton]: {
    eventContainer: 'referrals_list_view',
    eventTarget: 'filters_button',
    eventType: 'click',
    task: 'view',
  },
  [TrackReferralsEvent.FilterModalMount]: {
    eventContainer: 'referrals_list_view',
    eventTarget: 'filter_modal_mount',
    eventType: 'load',
    task: 'view',
  },
  [TrackReferralsEvent.FilterModalUnmount]: {
    eventContainer: 'referrals_filter_modal',
    eventTarget: '',
    eventType: 'click',
    task: 'view',
  },
  [TrackReferralsEvent.UpdateFilter]: {
    eventContainer: 'referrals_filter_modal',
    eventTarget: 'filter_item',
    eventType: 'click',
    task: '',
  },
  [TrackReferralsEvent.ClickClearAllFilters]: {
    eventContainer: 'referrals_filter_modal',
    eventTarget: 'clear_all_filters_button',
    eventType: 'click',
    task: 'select',
  },
};
